import type { AnnulOperation } from '@festina/configuration-general-types/configuration'
import { Yup } from '@festina-life/components'
import { typeUtils } from '@festina-life/core'
import { Button } from '@festina-life/ui/actions'
import { FullscreenSpinner, toast } from '@festina-life/ui/feedback'
import { FormikForm, FormikTextArea } from '@festina-life/ui/formikForms'
import { useTranslate } from '@festina-life/ui/hooks'
import { Stack, Typography } from '@festina-life/ui/structure'

import { OperationApproval } from '../../components/operationApproval/OperationApproval'
import { useAnnulOperationDescriptors } from '../../uiModels/annulOperations/useAnnulOperationDescriptors'
import { useOperationsContext } from '../../workspace/operationsContext'
import { useAnnulOperationKey } from './useAnnulOperationKey'

type AnnulReasonFormValues = {
  reason: string
}

const validate = typeUtils.createValidator<AnnulReasonFormValues>()

export const AnnulOperationDetailView = () => {
  const annulOperationKey = useAnnulOperationKey()
  const translate = useTranslate()
  const { allAnnulOperationsByKey, onAddOrUpdateOperation, isLoading } =
    useOperationsContext()

  const descriptors = useAnnulOperationDescriptors()
  const annulOperation = allAnnulOperationsByKey.get(annulOperationKey)

  if (isLoading) {
    return <FullscreenSpinner position="relative" />
  }

  if (!annulOperation) {
    return (
      <Typography className="p-4">
        {translate('annulOperation.noOperation')}
      </Typography>
    )
  }

  const { internalOperation } = annulOperation

  const descriptor = internalOperation.createOperationClassName
    ? descriptors[internalOperation.createOperationClassName]
    : undefined
  const entityClassName = descriptor
    ? descriptor.entityClassTranslation
    : translate('annulOperation.entityClass.unknown')

  const handleSaveReason = async (values: AnnulReasonFormValues) => {
    const nextOperation: AnnulOperation = {
      ...internalOperation,
      operationKey: annulOperation.isDraft
        ? internalOperation.operationKey
        : undefined,
      reason: values.reason,
    }

    try {
      await onAddOrUpdateOperation({
        operation: nextOperation,
        layer: annulOperation.layer,
        block: annulOperation.block,
        shouldOpenUnifiedBranchWorkspace: true,
      })
      toast({
        title: translate('annulOperationDetailView.reason.success'),
        variant: 'success',
      })
    } catch {
      toast({
        title: translate('annulOperationDetailView.reason.error'),
        variant: 'error',
      })
    }
  }

  return (
    <Stack className="flex-col w-full" gap="spacious" padding="spacious">
      <Stack className="flex-row items-center justify-between">
        <Typography size="xl">
          {translate('annulOperation.operationType')}
        </Typography>
        {!annulOperation.isDraft && (
          <OperationApproval
            changesetKey={annulOperation.changesetHistory?.changesetKey}
            operationKey={annulOperation.internalOperation.operationKey}
            actions={annulOperation.actions}
          />
        )}
      </Stack>
      <Stack className="flex-col max-w-xl" gap="spacious">
        <Stack className="flex-col">
          <Typography size="lg">{entityClassName}</Typography>
          <Typography>{internalOperation.name ?? '-'}</Typography>
        </Stack>
        <Stack className="flex-col" gap="normal">
          <Typography size="lg">
            {translate('annulOperation.reason.label')}
          </Typography>
          {/** We only support editing drafts. Inline editing of existent operation on the branch is not allowed
           * since we don't aggregate annul operations and each operation has its own viewer. */}
          {annulOperation.isDraft ? (
            <FormikForm
              initialValues={{
                reason: internalOperation.reason ?? '',
              }}
              validationSchema={Yup.object().shape({
                reason: Yup.string(),
              })}
              onSubmit={handleSaveReason}
              enableReinitialize
            >
              {(formValues) => (
                <>
                  <FormikTextArea
                    name={validate('reason')}
                    label={translate('annulOperation.reason.label')}
                    placeholder={translate('annulOperation.reason.emptyState')}
                  />
                  <Button
                    type="submit"
                    isLoading={formValues.isSubmitting}
                    className="mt-2 w-fit"
                  >
                    {translate('annulOperationDetailView.reason.submit.label')}
                  </Button>
                </>
              )}
            </FormikForm>
          ) : (
            <Typography>
              {internalOperation.reason ??
                translate('annulOperation.reason.emptyState')}
            </Typography>
          )}
        </Stack>
      </Stack>
    </Stack>
  )
}
